import { Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { AggregateRowModel, ColumnModel, FilterSettingsModel, PageSettingsModel } from '@syncfusion/ej2-angular-grids';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { InventoryService } from '../../../pick-plan/services/inventory.service';
import { ReasonCodeChangerPopupComponent } from './reason-code-changer-popup/reason-code-changer-popup.component';

interface MassBalanceEntry {
  date?: string;
  itemName?: string;
  productCode?: string;
  packoutCode?: string;
  reason?: string;
  balance?: number;
}

const MOBILE_PAGE_SIZE = 10;

@Component({
  selector: 'mass-balance',
  templateUrl: './mass-balance.component.html',
  styleUrls: ['./mass-balance.component.scss']
})
export class MassBalanceComponent implements OnInit {

  @Input() lotId: number;

  @ViewChild('dateTemplate', { static: true }) public dateTemplate: any;
  @ViewChild('reasonTemplate', { static: true }) public reasonTemplate: any;

  public faPen = faPen;
  public isLoading = true;
  public massBalance: MassBalanceEntry[] = [];
  public reasonCodes: any[] = [];
  public columns: ColumnModel[] = [];
  public mobileVisibleCount = MOBILE_PAGE_SIZE;

  public pageSettings: PageSettingsModel = { pageSize: 10, pageSizes: [10, 25, 50] };
  public filterSettings: FilterSettingsModel = { mode: 'Immediate', type: 'Excel' };
  public aggregates: AggregateRowModel[] = [{
    columns: [
      { field: 'reason', type: 'Count', footerTemplate: 'Total' },
      { field: 'balance', type: 'Sum', format: 'N2' }
    ]
  }];

  private destroyRef = inject(DestroyRef);

  constructor(
    private errorService: ErrorService,
    private inventoryService: InventoryService,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.inventoryService.getReasonCodes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: data => this.reasonCodes = data ?? [],
      error: err => this.errorService.handleError(err)
    });
    this.reload();
  }

  reload() {
    this.isLoading = true;
    this.inventoryService.getLotMassBalance(this.lotId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: MassBalanceEntry[]) => {
        this.massBalance = [...(data ?? [])].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        this.setColumns();
        this.mobileVisibleCount = MOBILE_PAGE_SIZE;
        this.isLoading = false;
      }, error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    });
  }

  editReason(entry: MassBalanceEntry) {
    ReasonCodeChangerPopupComponent.open(this.dialog, { record: entry, reasonCodes: this.reasonCodes })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(saved => {
        // The dialog updates the entry in place; re-emit so the grid re-renders it.
        if (saved)
          this.massBalance = [...this.massBalance];
      });
  }

  showMoreMobile() {
    this.mobileVisibleCount += MOBILE_PAGE_SIZE;
  }

  trackByEntry(index: number) {
    return index;
  }

  private setColumns() {
    const includeProduct = this.massBalance.some(x => !!x.productCode);
    const includePackout = this.massBalance.some(x => !!x.packoutCode);

    this.columns = [
      { field: 'date', headerText: 'Date', template: this.dateTemplate, autoFit: true },
      ...(includeProduct ? [<ColumnModel>{ field: 'itemName', headerText: 'Product', clipMode: 'EllipsisWithTooltip', autoFit: true }] : []),
      ...(includePackout ? [<ColumnModel>{ field: 'packoutCode', headerText: 'Packout Code', autoFit: true }] : []),
      { field: 'reason', headerText: 'Reason', template: this.reasonTemplate, autoFit: true },
      { field: 'balance', headerText: 'Balance', format: 'N2', textAlign: 'Right', autoFit: true }
    ];
  }
}
